export enum SolutionType {
  VOICE       = 'Voice',
  INTERNET    = 'Business Internet',
  IOT         = 'IoT / Telematics',
  TABLET      = 'Tablet / Mobile Data',
  PRO_INSTALL = 'Professional Installation',
  FEATURES    = 'Features',
}

export type DeviceFinancingTerm = 'FRP' | '24' | '30' | '33' | '36';

export interface QuoteDevice {
  id:             string;
  name:           string;
  cost:           number;
  count:          number;
  financingTerm:  DeviceFinancingTerm;
  needsVerification?: boolean;
  verificationNote?:  string;
}

/** Returns the monthly cost of a device based on its financing term.
 *  FRP = Full Retail Price (no spreading — full cost charged as-is).
 *  All other terms divide (cost × count) by the number of months. */
export function deviceMrcCalc(d: QuoteDevice): number {
  if (d.financingTerm === 'FRP') return 0;
  const months = parseInt(d.financingTerm, 10);
  return months > 0 ? (d.cost * d.count) / months : d.cost * d.count;
}

/** Returns the ONE-TIME flat cost of a device — only non-zero for FRP devices. */
export function deviceOneTimeCalc(d: QuoteDevice): number {
  return d.financingTerm === 'FRP' ? d.cost * d.count : 0;
}

/** Returns the effective monthly discount for a promo.
 *  Formula: (value × lines) ÷ duration */
export function promoMonthlyCalc(p: { value: number; lines?: number; duration?: number; type: 'monthly' | 'onetime' }): number {
  if (p.type === 'onetime') return 0; // one-time handled separately
  const total    = p.value * (p.lines ?? 1);
  const duration = p.duration && p.duration > 0 ? p.duration : 1;
  return total / duration;
}

/** Returns the effective one-time discount for a promo. */
export function promoOnetimeCalc(p: { value: number; lines?: number; type: 'monthly' | 'onetime' }): number {
  if (p.type !== 'onetime') return 0;
  return p.value * (p.lines ?? 1);
}

export interface QuoteItem {
  id:       string;
  type:     SolutionType;
  planName: string;
  lines:    number;
  mrc:      number;
  devices:  QuoteDevice[];
  needsVerification?: boolean;
  verificationNote?:  string;
}

export interface Promo {
  id:        string;
  name:      string;
  type:      'monthly' | 'onetime';
  value:     number;
  duration?: number;
  lines?:    number;
  needsVerification?: boolean;
  verificationNote?:  string;
}

export interface SellerInfo {
  orgName: string;
  name:    string;
  title:   string;
  email:   string;
  phone:   string;
}

export interface AIInsights {
  narrative:          string;
  objectionHandling:  string[];
  industryBenefits:   string[];
  roiSlicing?:        string;
  carrierComparison?: {
    carrier:  string;
    strength: string;
    weakness: string;
    score:    number;
  }[];
}

export interface QuoteState {
  customerName: string;
  companyName?: string;
  hqState?:     string;
  term:         number;
  sellerInfo:   SellerInfo;
  items:        QuoteItem[];
  promos:       Promo[];
  analysis?:    { usagePatterns: Record<string, unknown>; financials: Record<string, unknown>; };
  insights?:    AIInsights;
}

export interface CarrierBreakdown {
  providerName:         string;
  monthlyServiceTotal:  number;
  monthlyHardwareTotal: number;
  taxesAndFees:         number;
  priorPeriodAdjustment: number;
  otherOneTimeCredits:  number;
  etfTotal:             number;
  totalDue:             number;
  deviceCounts: {
    phone:     number;
    tablet:    number;
    watch:     number;
    connected: number;
    other:     number;
  };
  needsReview:          boolean;
  reviewReason:         string;
  failed?:              boolean;
  errorMessage?:        string;
}

export interface MultiCarrierResult {
  carriers: CarrierBreakdown[];
  combined: {
    monthlyServiceTotal:  number;
    monthlyHardwareTotal: number;
    taxesAndFees:         number;
    etfTotal:             number;
    totalDue:             number;
    deviceCounts: {
      phone:     number;
      tablet:    number;
      watch:     number;
      connected: number;
      other:     number;
    };
  };
}

export interface AIQuoteResponse {
  items:              Omit<QuoteItem, 'id'>[];
  recommendedPromos:  Omit<Promo, 'id'>[];
  summary:            string;
  analysis?: {
    usagePatterns: Record<string, unknown>;
    financials:    Record<string, unknown>;
  };
  insights?: AIInsights;
}

export interface SavedQuote {
  id:           string;
  userId:       string;
  customerName: string;
  companyName:  string;
  createdAt:    Date;
  mrc:          number;
  term:         number;
  apiContent:   QuoteState;
}

export const PLAN_OPTIONS: Record<SolutionType, string[]> = {
  [SolutionType.VOICE]: [
    'Business Unlimited Essentials',
    'Business Unlimited Go',
    'Business Unlimited Advanced',
    'Business Unlimited Ultimate',
  ],
  [SolutionType.INTERNET]: [
    'Business Internet Starter',
    'Business Internet 100',
    'Business Internet 300',
    'Business Internet 500',
  ],
  [SolutionType.IOT]: [
    'IoT Connect 1GB',
    'IoT Fleet 3GB',
    'Connected Device',
    'IoT Enterprise',
  ],
  [SolutionType.TABLET]: [
    'Business Tablet Plus',
    'Tablet Data 10GB',
    'Tablet Unlimited',
  ],
  [SolutionType.PRO_INSTALL]: [
    'Standard Installation',
    'Premium Installation',
    'Enterprise Deployment',
  ],
  [SolutionType.FEATURES]: [],
};

export const DEFAULT_MRC: Record<string, number> = {
  'Business Unlimited Essentials': 25,
  'Business Unlimited Go':         35,
  'Business Unlimited Advanced':   45,
  'Business Unlimited Ultimate':   55,
  'Business Internet Starter':     60,
  'Business Internet 100':         90,
  'Business Internet 300':        150,
  'Business Internet 500':        220,
  'IoT Connect 1GB':               10,
  'IoT Fleet 3GB':                 20,
  'Connected Device':              15,
  'IoT Enterprise':                30,
  'Business Tablet Plus':          25,
  'Tablet Data 10GB':              20,
  'Tablet Unlimited':              30,
  'Standard Installation':         99,
  'Premium Installation':         199,
  'Enterprise Deployment':        499,
};

export const DEVICE_CATALOG = [
  { name: 'Samsung Galaxy S25',    cost: 0    },
  { name: 'iPhone 16',             cost: 0    },
  { name: 'Samsung Galaxy A55',    cost: 0    },
  { name: 'Google Pixel 9',        cost: 0    },
  { name: 'Samsung Tab S9',        cost: 22   },
  { name: 'iPad 10th Gen',         cost: 18   },
  { name: 'NETGEAR M6 Hotspot',    cost: 15   },
  { name: 'Franklin T10 Hotspot',  cost: 10   },
  { name: 'OBD-II Tracker',        cost: 8    },
  { name: 'Asset Tracker',         cost: 5    },
  { name: 'Apple Watch Series 9',  cost: 10   },
];
